import { MicCapture, Pcm16Player, WorkerConnection } from '@100mslive/call-scheduler';
import { ClaimStatus } from '@compass/client/types/call';
import { TwilioCallStatus } from '@constants/twilioCallStatus';
import type { StackScope } from '@hooks/useStackableCases';
import { useStackableCases } from '@hooks/useStackableCases';
import { useStore } from '@store/index';
import { isAxiosError } from 'axios';
import {
  AudioLines,
  ExternalLink,
  Maximize2,
  Mic,
  MicOff,
  Minus,
  Phone,
  PhoneOff,
  X,
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { addSentryBreadcrumb, captureSentryException, captureSentryMessage } from '@/config/sentry';
import { DIALPAD_BUTTONS, LETTER_TO_DTMF_MAP } from '@/constants/dtmf';
import { isIvrAssistSupported } from '@/constants/ivrAssist';
import { type CallQualitySample, useCallQuality } from '@/hooks/useCallQuality';
import { playGrantCue, playRevokeCue, startRingbackCue, stopRingbackCue } from '@/utils/audio/cues';
import { stackTabLabel } from '@/utils/callStack';
import { isCallButtonDisabled, shouldShowNoiseCancellationToggle } from '@/utils/dialpadCallGate';
import { convertToDTMF } from '@/utils/dtmf';
import { buildProxiedLane } from '@/utils/proxiedLane';
import { claimTeardownOnce } from '@/utils/teardownGuard';

import { getTwilioCallStatusStyle } from '../config/twilio.config';
import { pickCallerId, providerLabel, resolveActiveProvider } from '../constants/voiceProviders';
import { useCreateCall } from '../hooks/useCalls';
import { useConfirmPageUnload } from '../hooks/useConfirmPageUnload';
import { useProgramPhoneNumbers } from '../hooks/useProgramPhoneNumbers';
import { callService } from '../services/api';
import { KrispAudioProcessor } from '../services/krispAudioProcessor';
import { isVoiceSupported, setActiveVoiceProvider, voiceService } from '../services/voice';
import { CallQualityPanel } from './CallQualityPanel';
import { CallStackSummary } from './CallStackSummary';
import { MosIndicator } from './MosIndicator';
import { ProviderToggle } from './ProviderToggle';
import { StackCallPanel, StackTabCount } from './StackCallPanel';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Checkbox } from './ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

// Claim polling cadence (BI-982). The backend does a single non-blocking
// scheduler poll per ClaimCall request and reports ASSIGNED or NO_CALL; the
// browser drives the loop, re-claiming on NO_CALL at this interval until the
// call is assigned or the overall budget elapses.
//
// These are intentionally client-owned: with the single-shot backend there is
// no server-side claim budget to mirror (the old call_scheduler claim_timeout
// was removed with the long-poll). Tune them here; they pace only this loop.
const CLAIM_POLL_INTERVAL_MS = 750;
const CLAIM_BUDGET_MS = 45_000;

// Minimal structural shape of a Twilio SDK error. Avoids depending on the
// SDK's namespace export and keeps the surface we touch small.
interface TelephonyError {
  code?: number;
  name?: string;
  message?: string;
  description?: string;
}

// HIPAA: Twilio SDK error strings are SDK-defined and do not contain PHI,
// but we never log phone numbers, names, DOB, or anything caller-supplied.
// This helper enforces that by passing only an explicit allowlist of fields.
// The reason the server rejected a call request (HTTP 400 — e.g. the previous
// human call has no disposition yet, service/calls.go). The reason travels in
// the error body's `error` field (`message` is the handler's static label);
// other statuses return null so the caller keeps its generic copy.
function serverRejectionMessage(error: unknown): string | null {
  if (!isAxiosError(error) || error.response?.status !== 400) return null;
  const data = error.response.data as { error?: string; message?: string } | undefined;
  return data?.error || data?.message || null;
}

function sanitizeTelephonyContext(context: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(context)) {
    if (value === undefined || value === null || value === '') continue;
    out[key] = value;
  }
  return out;
}

function captureTelephonyError(
  error: TelephonyError,
  context: { source: 'device' | 'call'; call_id: string; workflow_type?: string },
): void {
  const wrapped = new Error(
    `Twilio ${context.source} error: ${error?.name ?? 'Unknown'} (${error?.code ?? 'no-code'})`,
  );
  captureSentryException(
    wrapped,
    sanitizeTelephonyContext({
      source: context.source,
      call_id: context.call_id,
      workflow_type: context.workflow_type,
      error_code: error?.code,
      error_name: error?.name,
      error_message: error?.message,
      error_description: error?.description,
    }),
  );
}

// End Call Confirmation Dialog Component
interface EndCallConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}

function EndCallConfirmDialog({ open, onOpenChange, onConfirm }: EndCallConfirmDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>End Call</DialogTitle>
          <DialogDescription>Are you sure you want to end the current call?</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={onConfirm}>
            Yes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Request Talk Confirmation Dialog — shown when the human clicks the mic while
// the AI agent holds the mic. Confirming sends a requestTalk() to the server.
interface RequestTalkConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}

function RequestTalkConfirmDialog({
  open,
  onOpenChange,
  onConfirm,
}: RequestTalkConfirmDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Take over from the AI agent?</DialogTitle>
          <DialogDescription>
            The AI agent is on the call right now. When you take over, it stops speaking and your
            microphone turns on as soon as it hands the call to you — expect a brief pause.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Not yet
          </Button>
          <Button onClick={onConfirm}>Take over</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// What the dialpad shows once the call has ended: the call's own times, a read-only summary of the cases it covered, and the button that puts the dialpad away.
interface PostCallPanelProps {
  callStartTime: Date | null;
  callEndTime: Date | null;
  userEmail?: string;
  /** The case the call was placed from. */
  caseRecordId: string;
  /** The call that just ended; its stack is what the summary lists. */
  callId: string | null;
  /** Org and program captured when the dialpad opened. */
  scope: StackScope | null;
  onProceed: () => void;
}

function PostCallPanel({
  callStartTime,
  callEndTime,
  userEmail,
  caseRecordId,
  callId,
  scope,
  onProceed,
}: PostCallPanelProps) {
  const callDuration =
    callStartTime && callEndTime
      ? Math.floor((callEndTime.getTime() - callStartTime.getTime()) / 1000)
      : 0;

  const formatDuration = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="space-y-4">
      {/* Call details */}
      <div className="space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="text-muted-foreground">Call Start Time:</span>
          <span>{callStartTime ? callStartTime.toLocaleTimeString() : ''}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Call End Time:</span>
          <span>{callEndTime ? callEndTime.toLocaleTimeString() : ''}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Call Duration:</span>
          <span>{callDuration > 0 ? formatDuration(callDuration) : ''}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Call Assignee:</span>
          <span>{userEmail || '-'}</span>
        </div>
      </div>

      <div className="border-t pt-4">
        {/* Read-only: stacking closes at hangup. */}
        <div className="mb-4">
          <Label className="text-sm font-medium">Stacked cases:</Label>
          <div className="mt-1">
            <CallStackSummary
              caseRecordId={caseRecordId}
              callId={callId ?? undefined}
              scope={scope}
            />
          </div>
        </div>

        {/* Nothing is written here any more — the disposition and notes are
            saved from the Output section. This just puts the dialpad away. */}
        <Button onClick={onProceed} className="w-full" data-testid="dialpad-proceed">
          Proceed
        </Button>
      </div>
    </div>
  );
}

export function GlobalDialpad() {
  const {
    user,
    dialpad,
    closeDialpad,
    setDialpadMuted,
    setDialpadProvider,
    resetDialpadState,
    selectedOrganizationId,
    selectedProgramId,
    setConnectedCallAnchor,
    clearConnectedCallAnchor,
    setActiveCall,
  } = useStore();
  const createCallMutation = useCreateCall();
  const navigate = useNavigate();
  const location = useLocation();

  // Extract dialpad state
  const {
    isOpen,
    phoneNumber: initialPhoneNumber,
    caseRecordId,
    workflowName,
    workflowType,
    callIndex,
    returnPath,
    provider: selectedProvider,
    organizationId: dialpadOrganizationId,
    programId: dialpadProgramId,
  } = dialpad;

  // Stack requests use the org and program the call was placed in, never the
  // sidebar selection, which the agent can change mid-call.
  const stackScope = useMemo<StackScope | null>(
    () =>
      dialpadOrganizationId && dialpadProgramId
        ? { organizationId: dialpadOrganizationId, programId: dialpadProgramId }
        : null,
    [dialpadOrganizationId, dialpadProgramId],
  );

  // The dialpad's carrier control is derived entirely from the phone-number
  // registry: the groups it returns for this program are the carriers offered,
  // so a program with no registered number for a carrier is never offered it.
  const programNumbers = useProgramPhoneNumbers(selectedOrganizationId, selectedProgramId);
  const availableProviders = programNumbers.providers;
  const activeProvider = resolveActiveProvider(selectedProvider, availableProviders);

  // IVR Assist (proxied calling) is only offered for scheduler-supported workflow
  // types; every other workflow uses the legacy dialer exactly as before.
  const ivrAssistSupported = isIvrAssistSupported(workflowType);

  // Local UI state (not related to call status)
  const [phoneNumber, setPhoneNumber] = useState(initialPhoneNumber);
  const [inputValue, setInputValue] = useState(initialPhoneNumber);
  const [dtmfInput, setDtmfInput] = useState(''); // User's input (may contain letters)
  const [dtmfViewMode, setDtmfViewMode] = useState<'normal' | 'translated'>('normal'); // Toggle between views
  // The in-call side panel shows one thing at a time: keypad entry, or the
  // cases this conversation is covering.
  const [callPanelTab, setCallPanelTab] = useState<'dtmf' | 'stack'>('dtmf');
  const [isMinimized, setIsMinimized] = useState(false);
  const [position, setPosition] = useState({ x: window.innerWidth - 420, y: 100 });
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef<HTMLDivElement>(null);
  const dragStartPos = useRef({ x: 0, y: 0 });
  const dialpadDimensions = useRef<{ width: number; height: number } | null>(null);

  // Local call state management
  const [isCallInProgress, setIsCallInProgress] = useState(false);
  const [callStatus, setCallStatus] = useState<TwilioCallStatus>(TwilioCallStatus.IDLE);
  const [callMuted, setCallMuted] = useState(false);
  // Talk-grant state for multi-attach human calls. On a call where one
  // attachment talks at a time, the server flips this connection's mic via
  // GRANT_TALK / REVOKE_TALK control frames (surfaced as the SDK's onGrant /
  // onRevoke callbacks). 'revoked' = the AI agent took over: the human's mic is
  // auto-muted and a popover marks the AI as speaking; clicking the mic requests
  // it back (after a confirm). 'granted' = the human holds the mic (auto-unmuted).
  // 'idle' = not applicable (no grant/revoke seen this call).
  const [talkState, setTalkState] = useState<'idle' | 'granted' | 'revoked'>('idle');
  const [ncEnabled, setNcEnabled] = useState(() => voiceService().isNoiseCancellationEnabled());
  const [ncBusy, setNcBusy] = useState(false);
  // Transport selection (BI-982). Unchecked (default) → legacy Twilio Voice SDK;
  // checked → new proxied/WebSocket audio pipeline (opt-in). Persists for the session.
  const [useProxiedDialer, setUseProxiedDialer] = useState(false);
  // Read at render time: setActiveVoiceProvider runs before a call is in
  // progress, and the toggle only renders while one is.
  const showNcToggle = shouldShowNoiseCancellationToggle({
    proxied: useProxiedDialer,
    providerSupportsIt: voiceService().supportsNoiseCancellation(),
  });
  // Guards the Start button against duplicate clicks during the async start.
  const [isStarting, setIsStarting] = useState(false);
  // Microphone permission gate. Every call (legacy or proxied) needs mic access,
  // so we probe getUserMedia when the dialpad mounts. Only 'granted' enables the
  // Call button — the gate is `micPermission !== 'granted'`, so 'checking' blocks
  // it too, deliberately: a dial started before the probe answers can fail on a
  // missing mic after the call record already exists. 'denied'/'unsupported'
  // additionally show a warning strip explaining why the button is dead.
  const [micPermission, setMicPermission] = useState<
    'checking' | 'granted' | 'denied' | 'unsupported'
  >('checking');
  // ──────────────────────────────────────────────────────────────────────────
  // Proxied-call lifecycle (BI-982) — how the refs below map to call state:
  //
  //   idle ──startProxiedCall()──▶ connecting ──first caller audio──▶ connected
  //     ▲                              │                                  │
  //     └──────────────── teardown ◀───┴── end / server-close / error ────┘
  //
  //   • proxiedActiveRef   true from start() until teardown — the discriminator
  //                        every end/mute/DTMF/claim handler routes on.
  //   • conn/mic/player/krisp refs  the live data-plane resources; all non-null
  //                        only while connected, released together on teardown.
  //   • proxiedMutedRef    outbound-mic gate (MicCapture has no mute); also held
  //                        true for the duration of a DTMF tone.
  //   • ringbackActiveRef  true during 'connecting' (dialing) until first caller
  //                        audio stops the ringback exactly once.
  //   • talkState          multi-attach mic ownership: idle → granted/revoked.
  //
  //   Teardown is claimed exactly once via claimTeardownOnce(proxiedActiveRef)
  //   (see utils/teardownGuard) so the local hangup, the server close frame, and
  //   the echo that releasing resources emits can all fire but only one wins.
  // ──────────────────────────────────────────────────────────────────────────

  // Proxied-mode (call-scheduler data-plane) call resources. The SDK speaks
  // PCM16 end-to-end: MicCapture streams the mic, Pcm16Player plays the caller,
  // and WorkerConnection is the worker WebSocket. All null when not on a
  // proxied call.
  const proxiedConnRef = useRef<WorkerConnection | null>(null);
  const proxiedMicRef = useRef<MicCapture | null>(null);
  const proxiedPlayerRef = useRef<Pcm16Player | null>(null);
  // Krisp noise cancellation for the proxied mic path. Inserted into
  // MicCapture's AudioContext via its createProcessingNode hook; null when not
  // on a proxied call or when the browser can't run Krisp.
  const proxiedKrispRef = useRef<KrispAudioProcessor | null>(null);
  // True for the lifetime of a proxied call — the discriminator the end/mute/
  // DTMF handlers use to route to the proxied path instead of the legacy SDK.
  const proxiedActiveRef = useRef(false);
  // Mute is enforced by dropping outbound frames (MicCapture has no mute).
  const proxiedMutedRef = useRef(false);
  // Serializes proxied DTMF sends: each tone chains onto the previous so a rapid
  // or multi-tone sequence (e.g. a letter code's "*91") plays as distinct tones
  // instead of interleaving frames on the shared audio stream.
  const proxiedDtmfChainRef = useRef<Promise<void>>(Promise.resolve());
  // True while the dialing ringback cue is playing — from just after AckCall
  // until the first audio frame arrives from the caller (they answered). Guards
  // the one-shot stop in onAudio so it only fires on the first frame, and lets
  // teardown stop a still-ringing cue.
  const ringbackActiveRef = useRef(false);
  // Aborts in-flight claim/ack fetches when the call is torn down (user cancels,
  // hangs up, or the component unmounts) so a pending request doesn't keep
  // running in the background. Created per proxied call, aborted on teardown.
  const proxiedAbortRef = useRef<AbortController | null>(null);
  // Context for cancelling a proxied call that was submitted to the scheduler but
  // abandoned before connecting. Set once the call_id is known (after create),
  // cleared on connect (first caller audio) and after a cancel fires. Held in a
  // ref so the teardown choke point — including the unmount cleanup — can issue a
  // server-side CancelCall without a stale closure. Null = nothing to cancel.
  const proxiedCancelCtxRef = useRef<{
    organizationId: string;
    programId: string;
    caseRecordId: string;
    callId: string;
  } | null>(null);
  // Worker WebSocket attach URL from the claim response; saved across the
  // claim -> ack -> ws-connect steps. Cleared on teardown.
  const proxiedAttachUrlRef = useRef<string>('');
  const [showEndCallConfirm, setShowEndCallConfirm] = useState(false);
  // Confirm dialog shown when the human clicks the mic while the AI agent holds
  // the talk grant (talkState === 'revoked') — confirming sends requestTalk().
  const [showRequestTalkConfirm, setShowRequestTalkConfirm] = useState(false);
  const [showDisposition, setShowDisposition] = useState(false);
  const [currentCallId, setCurrentCallId] = useState<string | null>(null);
  // Mirrors currentCallId for the teardown callbacks, which are memoized on refs
  // rather than state so they stay stable across a call's lifetime — reading the
  // state there would close over whatever it was when the callback was created.
  const currentCallIdRef = useRef<string | null>(null);
  useEffect(() => {
    currentCallIdRef.current = currentCallId;
  }, [currentCallId]);
  const [callStartTime, setCallStartTime] = useState<Date | null>(null);
  const [callEndTime, setCallEndTime] = useState<Date | null>(null);
  // The payer has answered: set when the header flips to "Connected" (the voice
  // `accept` event, or the first caller audio on a proxied call) and kept through
  // a reconnect. Stacking waits for it, since hermes cuts each stacked case's
  // slice from the recording start, which does not exist while the call rings.
  const callConnected = callStartTime !== null;

  // The Stack call tab's count needs the list while DTMF is open too, so the 5s
  // poll runs here for as long as the in-call dialpad is on screen: not after
  // hangup, not while minimized. The panel reads the same cache entry.
  const stackListLive = isCallInProgress && !showDisposition && !isMinimized;
  const stackList = useStackableCases(
    stackScope,
    caseRecordId ?? '',
    stackListLive ? (currentCallId ?? undefined) : undefined,
  );
  const stackLeft = stackListLive ? (stackList.data?.stackable_count ?? 0) : 0;

  // Call quality state — fed by `voiceService().on('sample', ...)` during active calls
  const { callQuality, onSample, onWarning, onWarningCleared, resetCallQuality } = useCallQuality();
  const [showQualityPanel, setShowQualityPanel] = useState(false);

  // Mirror the live Twilio call into the store so the case page can drive the Human Call
  // Script tab open/close in real time (no backend refetch lag). "Live" = any non-terminal
  // Twilio status (from the moment the call is placed through connected); cleared on
  // disconnect/cancel/idle. Session-scoped — not persisted, and a reload ends the call.
  useEffect(() => {
    const isLive =
      callStatus !== TwilioCallStatus.IDLE &&
      callStatus !== TwilioCallStatus.DISCONNECTED &&
      callStatus !== TwilioCallStatus.CANCELLED;
    setActiveCall(
      isLive && currentCallId && caseRecordId ? { callId: currentCallId, caseRecordId } : null,
    );
  }, [callStatus, currentCallId, caseRecordId, setActiveCall]);

  // Reset phone number input every time the dialpad opens
  useEffect(() => {
    if (isOpen) {
      setPhoneNumber(initialPhoneNumber);
      setInputValue(initialPhoneNumber);
    }
  }, [isOpen, initialPhoneNumber]);

  // Probe microphone permission whenever the dialpad opens. We request a stream
  // purely to check access, then immediately stop every track so we don't hold
  // the mic open before a call actually starts. While a call is in progress the
  // mic is already in use, so skip the probe to avoid contending for the device.
  useEffect(() => {
    if (!isOpen || isCallInProgress) {
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      setMicPermission('unsupported');
      return;
    }

    let cancelled = false;
    setMicPermission('checking');
    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then(stream => {
        stream.getTracks().forEach(track => track.stop());
        if (!cancelled) {
          setMicPermission('granted');
        }
      })
      .catch(() => {
        if (!cancelled) {
          setMicPermission('denied');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, isCallInProgress]);

  // The IVR Assist toggle persists for the session; if the dialpad reopens on a
  // workflow that doesn't support it, force it back off so a stale "on" can't
  // route an unsupported case to the proxied path.
  useEffect(() => {
    if (!ivrAssistSupported && useProxiedDialer) {
      setUseProxiedDialer(false);
    }
  }, [ivrAssistSupported, useProxiedDialer]);

  // Capture dialpad dimensions when it's first rendered
  useEffect(() => {
    if (dragRef.current && !dialpadDimensions.current && isOpen) {
      const rect = dragRef.current.getBoundingClientRect();
      dialpadDimensions.current = {
        width: rect.width,
        height: rect.height,
      };
    }
  }, [isOpen]);

  // Prevent tab close during active call
  useConfirmPageUnload(
    isCallInProgress || showDisposition,
    'You have an active call. Are you sure you want to leave? The call will be disconnected.',
  );

  // Handle dragging
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    // Only drag from the header
    if (!(e.target as HTMLElement).closest('.dialpad-header')) return;

    setIsDragging(true);
    dragStartPos.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;

      const newX = e.clientX - dragStartPos.current.x;
      const newY = e.clientY - dragStartPos.current.y;

      // Keep within viewport bounds
      const maxX = window.innerWidth - (dragRef.current?.offsetWidth || 400);
      const maxY = window.innerHeight - (dragRef.current?.offsetHeight || 100);

      setPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY)),
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  // Handle digit press
  const handleDigitPress = (digit: string) => {
    if (isCallInProgress) {
      // Send DTMF tone
      handleSendDTMF(digit);
      // Add to DTMF input for visual feedback
      setDtmfInput(prev => prev + digit);
    } else {
      // Add to phone number
      const newValue = inputValue + digit;
      setInputValue(newValue);
      setPhoneNumber(newValue);
    }
  };

  const handleBackspace = () => {
    if (isCallInProgress) {
      // Clear DTMF input during call
      setDtmfInput(prev => prev.slice(0, -1));
    } else if (inputValue) {
      // Clear phone number before call
      const newValue = inputValue.slice(0, -1);
      setInputValue(newValue);
      setPhoneNumber(newValue);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    setPhoneNumber(value);
  };

  const handleDtmfInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase(); // Convert to uppercase for consistency
    const prevLength = dtmfInput.length;

    // Allow letters, numbers, *, and #
    const allowedValue = value.replace(/[^A-Z0-9*#]/g, '');
    setDtmfInput(allowedValue);

    // If a new character was added, send the appropriate DTMF
    if (allowedValue.length > prevLength) {
      const newChar = allowedValue[allowedValue.length - 1];

      // Check if it's a letter that needs conversion
      if (LETTER_TO_DTMF_MAP[newChar]) {
        // Send the DTMF code for the letter (e.g., *91 for W)
        const dtmfCode = LETTER_TO_DTMF_MAP[newChar];
        // Send each character of the code separately
        for (const tone of dtmfCode) {
          handleSendDTMF(tone);
        }
      } else if (/[0-9*#]/.test(newChar)) {
        // It's a number or special character - send as-is
        handleSendDTMF(newChar);
      }
    }
  };

  // BV-2130 — who hung up.
  //
  // The browser is the only observer that sees every human call end on both
  // carriers, and it sees it before any carrier webhook lands. That head start is
  // what keeps the case.call.completed timeline entry attributed: that entry is
  // emitted by a change-stream watcher keyed on the call's status, against a
  // ledger with no backfill, so an attribution arriving after the status is lost
  // to it forever.
  //
  // The value is only ever a hint. The server fences it (human calls only, closed
  // vocabulary, never overwriting a carrier's verdict), and the carrier's own
  // signal replaces it when the two disagree.
  const reportTerminatedBy = useCallback(
    (callId: string | null, terminatedBy: 'rep' | 'payer' | 'unknown') => {
      if (!callId || !caseRecordId || !selectedOrganizationId || !selectedProgramId) return;
      void callService
        .updateCall({
          organization_id: selectedOrganizationId,
          program_id: selectedProgramId,
          caserecord_id: caseRecordId,
          call_id: callId,
          end_reason: { terminated_by: terminatedBy },
        })
        .catch(() => {
          // Best-effort by design. This runs inside teardown, and the carrier
          // still reports independently — a lost hint must never surface an
          // error toast at the moment the agent hangs up.
        });
    },
    [caseRecordId, selectedOrganizationId, selectedProgramId],
  );

  // True between the agent pressing End and the SDK's `disconnect` firing.
  //
  // The legacy SDK seam cannot tell the two apart on its own: hangUp() makes the
  // SDK emit the very same `disconnect` a remote hangup emits, with no payload
  // (see services/voice/types.ts). So the discrimination has to be recorded at
  // the point of intent, which is the button handler.
  const legacyLocalHangupRef = useRef(false);

  // Handle making a call
  const handleMakeCall = useCallback(async () => {
    const dialedNumber = phoneNumber;

    if (!user?.email) {
      toast.error('User email not available. Please log in again.');
      return;
    }

    if (!dialedNumber) {
      toast.error('Please enter a phone number.');
      return;
    }

    if (!caseRecordId || !workflowName) {
      toast.error('Please open the dialpad from a case record.');
      return;
    }

    if (!selectedOrganizationId || !selectedProgramId) {
      toast.error('Organization and program must be selected.');
      return;
    }

    // Check browser support using Twilio's built-in method
    if (!isVoiceSupported()) {
      toast.error('Your browser does not support voice calls. Please use any other browser');
      return;
    }

    // Set to pending state immediately for better UX
    setIsCallInProgress(true);
    setCallStatus(TwilioCallStatus.PENDING);

    try {
      // Step 1: Get the outbound number for the chosen carrier.
      //
      // The registry is the only source, and pickCallerId draws only from the
      // selected carrier's group — so a caller ID is never presented to a carrier
      // that does not own it, which that carrier would reject.
      const fromNumber = pickCallerId(programNumbers.byProvider, activeProvider);
      if (!fromNumber) {
        throw new Error('No outbound phone numbers configured for this program');
      }

      // Ensure phone number is in E.164 format
      const formattedPhone = dialedNumber.startsWith('+') ? dialedNumber : `+${dialedNumber}`;

      const callResponse = await createCallMutation.mutateAsync({
        caserecord_id: caseRecordId,
        phone: formattedPhone,
        out_phone: fromNumber,
        provider: activeProvider,
      });

      if (!callResponse.call?.id) {
        throw new Error('Failed to create call record');
      }

      const newCallId = callResponse.call.id;
      setCurrentCallId(newCallId);

      // Step 2: Get access token using call ID
      const tokenResponse = await callService.generateAccessToken({
        organization_id: selectedOrganizationId,
        program_id: selectedProgramId,
        caserecord_id: caseRecordId,
        call_id: newCallId,
      });

      if (!tokenResponse.token) {
        throw new Error('Failed to get access token');
      }

      // The token response names the carrier that issued it, and a token only
      // works against that carrier. Point the seam at it before any further
      // voiceService() call in this flow reaches for the wrong one.
      setActiveVoiceProvider(tokenResponse.provider);

      // Step 3: Initialize the voice device with the token
      if (!voiceService().isCallInProgress()) {
        await voiceService().initializeWithToken(tokenResponse.token);
      }

      // The Krisp processor is attached during Device init. Sync the UI
      // state now so the toggle reflects reality on first render.
      setNcEnabled(voiceService().isNoiseCancellationEnabled());

      // Wire device-level observability. The SDK fires `error` for transport,
      // signaling, and token failures, and `tokenWillExpire` shortly before
      // the JWT TTL elapses — we refresh the token to survive long reconnects.
      // Track the most recent call error and reconnect state so a disconnect can
      // be attributed to a network/SDK failure when reporting to Sentry.
      let lastCallError: TelephonyError | null = null;
      let wasReconnecting = false;

      // Both levels arrive on one event, tagged with their source. Registered
      // before the call is placed so a failure during connect is still captured.
      voiceService().on('error', ({ source, error }) => {
        if (source === 'call') {
          lastCallError = error;
        }
        captureTelephonyError(error, {
          source,
          call_id: newCallId,
          workflow_type: workflowName,
        });
      });

      voiceService().on('tokenWillExpire', async () => {
        try {
          const refreshed = await callService.generateAccessToken({
            organization_id: selectedOrganizationId,
            program_id: selectedProgramId,
            caserecord_id: caseRecordId,
            call_id: newCallId,
          });
          if (refreshed.token) {
            voiceService().updateToken(refreshed.token);
            addSentryBreadcrumb('Voice access token refreshed', 'telephony', 'info', {
              call_id: newCallId,
            });
          }
        } catch (refreshError) {
          captureSentryException(
            refreshError instanceof Error ? refreshError : new Error('Token refresh failed'),
            { call_id: newCallId, source: 'tokenWillExpire' },
          );
        }
      });

      // Step 4: Make the call with Twilio SDK
      await voiceService().makeCall({
        to: formattedPhone,
        from: fromNumber,
        callId: newCallId, // Pass the MongoDB call ID
      });

      // A provider whose noise cancellation lives in its per-call mic only has
      // a state once the call is placed; the sync after initializeWithToken
      // above read it before that mic existed.
      setNcEnabled(voiceService().isNoiseCancellationEnabled());

      // Update mute state
      setCallMuted(false);

      // Setup call event handlers
      voiceService().on('accept', () => {
        setCallStatus(TwilioCallStatus.CONNECTED);
        setCallStartTime(new Date());
        if (newCallId) setConnectedCallAnchor(newCallId, performance.now());
        addSentryBreadcrumb('Twilio call connected', 'telephony', 'info', {
          call_id: newCallId,
          workflow_type: workflowName,
        });
      });

      voiceService().on('disconnect', () => {
        setCallStatus(TwilioCallStatus.DISCONNECTED);
        setIsCallInProgress(false);
        setCallMuted(false);
        setDtmfInput('');
        setCallEndTime(new Date());
        if (newCallId) clearConnectedCallAnchor(newCallId);

        // Who ended it (BV-2130). The ref is set by the End button and consumed
        // here, because this one event serves both a local and a remote hangup.
        // A disconnect that came with an error or mid-reconnect is a dropped
        // line, not a hangup by either party — claim neither.
        const locallyEnded = legacyLocalHangupRef.current;
        legacyLocalHangupRef.current = false;
        if (wasReconnecting || lastCallError) {
          reportTerminatedBy(newCallId, 'unknown');
        } else {
          reportTerminatedBy(newCallId, locallyEnded ? 'rep' : 'payer');
        }

        // If we disconnected mid-reconnect or with a recent error, this was
        // not a clean hangup — surface it to Sentry for triage.
        if (wasReconnecting || lastCallError) {
          captureSentryMessage(
            'Twilio call dropped unexpectedly',
            'error',
            sanitizeTelephonyContext({
              call_id: newCallId,
              workflow_type: workflowName,
              was_reconnecting: wasReconnecting,
              error_code: lastCallError?.code,
              error_name: lastCallError?.name,
              error_message: lastCallError?.message,
              error_description: lastCallError?.description,
            }),
          );
          toast.error('Call ended due to a connection issue.');
        }

        voiceService().destroy();
        setShowQualityPanel(false);
        setShowDisposition(true);
      });

      voiceService().on('cancel', () => {
        setCallStatus(TwilioCallStatus.CANCELLED);
        setIsCallInProgress(false);
        setCallMuted(false);
        setDtmfInput('');
        setCallEndTime(new Date());
        resetCallQuality();
        setShowQualityPanel(false);
        voiceService().destroy();
        setShowDisposition(true);
      });

      voiceService().on('reconnecting', (reconnectError: TelephonyError) => {
        setCallStatus(TwilioCallStatus.RECONNECTING);
        wasReconnecting = true;
        lastCallError = reconnectError;
        addSentryBreadcrumb(
          'Twilio call entered reconnecting state',
          'telephony',
          'warning',
          sanitizeTelephonyContext({
            call_id: newCallId,
            workflow_type: workflowName,
            error_code: reconnectError?.code,
            error_name: reconnectError?.name,
            error_message: reconnectError?.message,
          }),
        );
      });

      voiceService().on('reconnected', () => {
        setCallStatus(TwilioCallStatus.CONNECTED);
        // A reconnect can drop a provider's noise cancellation; show what is true now.
        setNcEnabled(voiceService().isNoiseCancellationEnabled());
        wasReconnecting = false;
        lastCallError = null;
        addSentryBreadcrumb('Twilio call recovered after reconnect', 'telephony', 'info', {
          call_id: newCallId,
        });
      });

      voiceService().on('warning', ({ name, data }) => {
        onWarning(name);
        addSentryBreadcrumb(`Twilio call warning: ${name}`, 'telephony', 'warning', {
          call_id: newCallId,
          warning: name,
          data: typeof data === 'object' ? JSON.stringify(data) : String(data ?? ''),
        });
      });

      voiceService().on('warningCleared', (name: string) => {
        onWarningCleared(name);
        addSentryBreadcrumb(`Twilio call warning cleared: ${name}`, 'telephony', 'info', {
          call_id: newCallId,
          warning: name,
        });
      });

      // Collect per-second WebRTC quality samples for the network debugger.
      voiceService().on('sample', (sample: CallQualitySample) => {
        onSample(sample);
      });

      voiceService().on('mute', (isMuted: boolean) => {
        setCallMuted(isMuted);
      });

      // Clear DTMF input when starting a new call
      setDtmfInput('');
    } catch (error) {
      let errorMessage = 'Failed to initiate call';
      const serverMessage = serverRejectionMessage(error);
      if (serverMessage) {
        errorMessage = serverMessage;
      } else if (error instanceof Error) {
        // Check for permission-related errors
        if (error.message.includes('Permission') || error.message.includes('NotAllowed')) {
          errorMessage =
            'Microphone permission denied. Please allow microphone access and try again.';
        } else if (error.message.includes('NotFound') || error.message.includes('Device')) {
          errorMessage = 'No microphone found. Please connect a microphone.';
        } else if (error.message.includes('getUserMedia')) {
          errorMessage = 'Failed to access microphone. Please check your browser settings.';
        } else {
          errorMessage = error.message;
        }
      }

      toast.error(errorMessage);
      // Reset states on error
      setIsCallInProgress(false);
      setCallStatus(TwilioCallStatus.IDLE);
      resetCallQuality();
      setShowQualityPanel(false);
      resetDialpadState();
    }
  }, [
    phoneNumber,
    user?.email,
    caseRecordId,
    workflowName,
    callIndex,
    createCallMutation,
    resetDialpadState,
    resetCallQuality,
    selectedOrganizationId,
    selectedProgramId,
    onSample,
    onWarning,
    onWarningCleared,
    reportTerminatedBy,
  ]);

  // Release all proxied-call resources (mic, Krisp, player, WS). Idempotent and
  // UI-agnostic — callers own the state transition. Closing the WS here is safe
  // even when the close was server-initiated (the socket is already closing).
  const releaseProxiedResources = useCallback(() => {
    // Stop the dialing ringback if it's still playing (call torn down before
    // the caller answered, e.g. local hangup, no-answer, or an error).
    stopRingbackCue();
    ringbackActiveRef.current = false;
    // Cancel any in-flight claim/ack fetch so it doesn't run after teardown.
    proxiedAbortRef.current?.abort();
    proxiedAbortRef.current = null;
    // If the call was submitted to the scheduler but abandoned before connecting
    // (budget exhausted, user cancelled, page/route unmount), tell the scheduler
    // to drop it so it doesn't linger in the queue. Cleared on connect, so a
    // normally-connected hangup is a no-op here. Best-effort + idempotent.
    const cancelCtx = proxiedCancelCtxRef.current;
    if (cancelCtx) {
      proxiedCancelCtxRef.current = null;
      void callService
        .cancelCall({
          organization_id: cancelCtx.organizationId,
          program_id: cancelCtx.programId,
          caserecord_id: cancelCtx.caseRecordId,
          call_id: cancelCtx.callId,
        })
        .catch(() => {
          // Best-effort: the in-flight fetch is already aborted and the call is
          // idempotently cancellable; swallow so teardown never throws.
        });
    }
    proxiedMicRef.current?.stop();
    proxiedMicRef.current = null;
    proxiedKrispRef.current?.stopProcessingNode();
    proxiedKrispRef.current = null;
    proxiedConnRef.current?.close();
    proxiedConnRef.current = null;
    proxiedPlayerRef.current?.close();
    proxiedPlayerRef.current = null;
    proxiedAttachUrlRef.current = '';
  }, []);

  // Single source of truth for tearing a proxied call down exactly once. The
  // local hangup (endProxiedCall), the start-error path, and the server close
  // frame can all fire — and releasing resources itself emits a close frame that
  // echoes back into handleProxiedClose. The first caller flips the flag and
  // wins; everyone else (including that echo) sees it cleared and bails. Safe
  // without locking because JS callbacks run to completion on a single thread.
  const claimProxiedTeardown = useCallback(() => claimTeardownOnce(proxiedActiveRef), []);

  // Unmount safety net: if the dialpad unmounts mid proxied call (e.g. SPA
  // navigation), the explicit end/close paths won't run — so abort any in-flight
  // claim/ack fetch and release the mic/WS/player/ringback here. claimProxiedTeardown
  // first makes the WS-close echo a no-op so it can't setState on an unmounted
  // component. Both callbacks are stable (empty deps), so this runs only on unmount.
  useEffect(() => {
    return () => {
      claimProxiedTeardown();
      releaseProxiedResources();
    };
  }, [claimProxiedTeardown, releaseProxiedResources]);

  // Server-initiated close of the worker WebSocket. The JS client never sees
  // Twilio events directly: the server consumes them and translates terminal
  // call states into close frames — "call ended" (clean hangup) or
  // "telephony <status>" (busy/failed/no-answer/canceled). A close with no
  // reason is an abrupt drop (network loss / pod restart), not a Twilio event.
  const handleProxiedClose = useCallback(
    ({ clean, reason }: { clean: boolean; code: number; reason: string }) => {
      // A local hangup (endProxiedCall) or the start-error path already claimed
      // teardown and released resources; the close frame they triggered is an
      // echo we ignore.
      if (!claimProxiedTeardown()) return;

      // Winning the teardown claim here means the far end ended it, not us
      // (BV-2130) — the local hangup would have claimed it first. The close
      // reason separates the two ways that happens: a clean "call ended" is the
      // payer hanging up, while a drop or a never-connected dial is attributable
      // to nobody.
      reportTerminatedBy(
        currentCallIdRef.current,
        clean && !reason.startsWith('telephony ') ? 'payer' : 'unknown',
      );

      releaseProxiedResources();
      setCallMuted(false);
      setTalkState('idle');
      setShowRequestTalkConfirm(false);
      setDtmfInput('');
      setCallEndTime(new Date());
      setShowEndCallConfirm(false);
      setIsCallInProgress(false);
      setCallStatus(TwilioCallStatus.DISCONNECTED);

      if (reason.startsWith('telephony ')) {
        // The call dialed but reached a terminal Twilio status without
        // connecting. Surface it, then let the agent disposition the attempt.
        const status = reason.slice('telephony '.length);
        toast.error(`Call did not connect (${status}).`);
      } else if (!clean) {
        // 1006 — torn without a close frame: crash or network loss.
        toast.error('Call connection lost.');
      }
      // reason === 'call ended' (or any other clean close) is a normal remote
      // hangup and needs no toast.

      // Move to the post-call disposition flow (mirrors the legacy disconnect).
      setShowDisposition(true);
    },
    [claimProxiedTeardown, releaseProxiedResources, reportTerminatedBy],
  );

  // Start a proxied (WebSocket) call: create the record, take mic permission,
  // and bring up the browser audio pipeline. The agent-ws transport to the call
  // scheduler is wired in a follow-up; for now a local loopback lets us verify
  // the pipeline end-to-end.
  const startProxiedCall = useCallback(async () => {
    if (!user?.email) {
      toast.error('User email not available. Please log in again.');
      return;
    }
    if (!phoneNumber) {
      toast.error('Please enter a phone number.');
      return;
    }
    if (!caseRecordId || !workflowName) {
      toast.error('Please open the dialpad from a case record.');
      return;
    }
    if (!selectedOrganizationId || !selectedProgramId) {
      toast.error('Organization and program must be selected.');
      return;
    }

    setIsStarting(true);
    setIsCallInProgress(true);
    setCallStatus(TwilioCallStatus.CONNECTING);

    try {
      proxiedActiveRef.current = true;
      proxiedMutedRef.current = false;
      proxiedDtmfChainRef.current = Promise.resolve();
      // Abort signal for the claim/ack fetches; teardown aborts it.
      const abortController = new AbortController();
      proxiedAbortRef.current = abortController;
      const abortSignal = abortController.signal;
      setTalkState('idle');
      setShowRequestTalkConfirm(false);
      // Step 1: create the call record so we have a call_id (the claim route is
      // keyed by it, and the disposition flow needs it to update the call).
      // The registry is the only source, and pickCallerId draws only from the
      // selected carrier's group — so a caller ID is never presented to a carrier
      // that does not own it, which that carrier would reject.
      //
      // IVR Assist has no carrier control of its own; it reuses the carrier the
      // dialpad already resolved, which for a program the agent has never chosen
      // one for is that program's first available carrier in the configured order.
      const fromNumber = pickCallerId(programNumbers.byProvider, activeProvider);
      if (!fromNumber) {
        throw new Error('No outbound phone numbers configured for this program');
      }
      const formattedPhone = phoneNumber.startsWith('+') ? phoneNumber : `+${phoneNumber}`;

      // The scheduler lane is generated here in the browser and sent on the
      // create request; the same value is echoed back on claim/ack so the worker
      // poll binds to the same lane and only this browser can claim the call
      // (routes it back here).
      const lane = buildProxiedLane(user?.id);

      // proxied: true makes the backend submit the created call to the
      // call-scheduler queue (under its own call_id as the agent id) so this
      // browser worker can claim it next.
      const callResponse = await createCallMutation.mutateAsync({
        caserecord_id: caseRecordId,
        phone: formattedPhone,
        out_phone: fromNumber,
        // Sent so the carrier and the caller ID agree: fromNumber came from this
        // carrier's own group, and the server refuses a number registered to another.
        provider: activeProvider,
        proxied: true,
        lane,
      });
      const newCallId = callResponse.call?.id;
      if (!newCallId) {
        throw new Error('Failed to create call record');
      }
      setCurrentCallId(newCallId);
      // Arm the cancel context: from here until the call connects, an abandon
      // (claim budget exhausted, user hangup, or unmount) issues a server-side
      // CancelCall via the teardown choke point so the queued call doesn't linger.
      proxiedCancelCtxRef.current = {
        organizationId: selectedOrganizationId,
        programId: selectedProgramId,
        caseRecordId,
        callId: newCallId,
      };

      // Step 2: claim the call from the call-scheduler via the Compass broker.
      // Each request triggers exactly one non-blocking scheduler poll and reports
      // ASSIGNED (call_id set) or NO_CALL (nothing ready yet). We drive the loop
      // here: re-claim on NO_CALL at a fixed cadence until assigned or the budget
      // elapses. The pod-routed attach URL is issued by the ack below, once the
      // worker is bound. The agent id is derived server-side from the call's
      // workflow type, so the browser never sends it.
      let claimedCallId = '';
      const claimDeadline = Date.now() + CLAIM_BUDGET_MS;
      for (;;) {
        // Bail if the call was torn down (e.g. user hung up) while we were polling.
        if (!proxiedActiveRef.current) return;
        const claim = await callService.claimCall(
          {
            organization_id: selectedOrganizationId,
            program_id: selectedProgramId,
            caserecord_id: caseRecordId,
            call_id: newCallId,
            lane,
          },
          { signal: abortSignal },
        );
        if (claim.status === ClaimStatus.CLAIM_STATUS_ASSIGNED) {
          claimedCallId = claim.call_id || newCallId;
          break;
        }
        // NO_CALL — not ready yet. Give up once the budget is exhausted; teardown
        // then both aborts the in-flight fetch and issues a server-side CancelCall
        // for the still-queued call (see releaseProxiedResources).
        if (Date.now() >= claimDeadline) {
          throw new Error('Call could not be connected in time. Please try again.');
        }
        await new Promise<void>(resolve => {
          setTimeout(resolve, CLAIM_POLL_INTERVAL_MS);
        });
      }

      // Step 3: ack using the same call_id sent to the create/calls API. On
      // success the scheduler dials the customer and returns the pod-routed
      // WebSocket attach URL; we only open the WebSocket once this succeeds.
      const ack = await callService.ackCall(
        {
          organization_id: selectedOrganizationId,
          program_id: selectedProgramId,
          caserecord_id: caseRecordId,
          call_id: newCallId,
          lane,
        },
        { signal: abortSignal },
      );
      proxiedAttachUrlRef.current = ack.attach_url ?? '';

      // Step 4: open the data-plane WebSocket using the ack's attach_url, then
      // stream the mic. The SDK is PCM16 end-to-end (no µ-law bridge):
      // Pcm16Player plays the caller's audio, MicCapture sends the mic.
      if (!proxiedAttachUrlRef.current) {
        throw new Error('ack returned no attach_url');
      }
      // The scheduler is now dialing the customer. Play a ringback until the
      // first audio frame arrives from the caller (i.e. they answered) — see
      // onAudio below, which stops it. Teardown also stops it if the call ends
      // before anyone picks up.
      startRingbackCue();
      ringbackActiveRef.current = true;
      // Dialing: header shows "Ringing". It flips to "Connected" only when the
      // first caller audio arrives (onAudio below) — the same signal that stops
      // the ringback — so the status and the ring cue stay in sync (rather than
      // flipping to Connected at pipeline-ready, while the ring is still playing).
      setCallStatus(TwilioCallStatus.RINGING);
      const player = new Pcm16Player();
      proxiedPlayerRef.current = player;
      const conn = await WorkerConnection.connect({
        attachUrl: proxiedAttachUrlRef.current,
        callId: claimedCallId,
        onAudio: (pcm16, sampleRate) => {
          // First caller audio means they answered: stop the ringback AND flip to
          // Connected (both off this one signal, so they stay in sync), set the
          // start time, and disarm the cancel context (the call is now live, not
          // abandoned). Guarded so it runs exactly once.
          if (ringbackActiveRef.current) {
            ringbackActiveRef.current = false;
            stopRingbackCue();
            proxiedCancelCtxRef.current = null;
            setCallStatus(TwilioCallStatus.CONNECTED);
            setCallStartTime(new Date());
          }
          player.play(pcm16, sampleRate);
        },
        // Talk handoff: the human (master) was granted the mic and may speak.
        // Play a rising cue and auto-unmute so they're live immediately.
        onGrant: () => {
          playGrantCue();
          setTalkState('granted');
          proxiedMutedRef.current = false;
          setCallMuted(false);
          setDialpadMuted(false);
        },
        // The AI agent (slave) took the mic. Play a falling cue, auto-mute the
        // human, and surface the "AI Agent is speaking" popover over the mic
        // button. Clicking the mic now requests the mic back (with a confirm).
        onRevoke: () => {
          playRevokeCue();
          setTalkState('revoked');
          proxiedMutedRef.current = true;
          setCallMuted(true);
          setDialpadMuted(true);
        },
        onClose: handleProxiedClose,
      });
      proxiedConnRef.current = conn;

      // Stream the mic (prompts for permission). Starting after connect means
      // no frames are produced early; mute drops frames before sending. Krisp
      // noise cancellation is inserted into MicCapture's AudioContext via the
      // createProcessingNode hook; if Krisp is unsupported the factory returns
      // null and MicCapture streams the raw mic.
      const krisp = new KrispAudioProcessor();
      proxiedKrispRef.current = krisp;
      const mic = await MicCapture.start(
        pcm16 => {
          if (proxiedMutedRef.current) return;
          conn.sendAudio(pcm16);
        },
        {
          createProcessingNode: (ctx, source) => krisp.createProcessingNode(ctx, source),
        },
      );
      proxiedMicRef.current = mic;
      setNcEnabled(krisp.isEnabled());

      setCallMuted(false);
      setDtmfInput('');
      // Status stays RINGING here; it flips to CONNECTED on first caller audio
      // (onAudio above) so the header and the ringback cue stay in sync.
    } catch (error) {
      // An aborted claim/ack fetch means the call was already torn down (user
      // cancel, hangup, or unmount). That path handled state + cleanup, so bail
      // silently — no toast, no double teardown. (axios → CanceledError.)
      if (
        error instanceof Error &&
        (error.name === 'CanceledError' || error.name === 'AbortError')
      ) {
        return;
      }
      let errorMessage = 'Failed to start call';
      const serverMessage = serverRejectionMessage(error);
      if (serverMessage) {
        errorMessage = serverMessage;
      } else if (error instanceof Error) {
        if (
          error.name === 'NotAllowedError' ||
          error.message.includes('Permission') ||
          error.message.includes('NotAllowed')
        ) {
          errorMessage =
            'Microphone permission denied. Please allow microphone access and try again.';
        } else if (
          error.name === 'NotFoundError' ||
          error.message.includes('NotFound') ||
          error.message.includes('Device')
        ) {
          errorMessage = 'No microphone found. Please connect a microphone.';
        } else if (error.message === 'AUDIOWORKLET_UNSUPPORTED') {
          errorMessage =
            'Your browser does not support the new calling mode. Please switch to the legacy dialer.';
        } else {
          errorMessage = error.message;
        }
      }
      toast.error(errorMessage);
      // Claim teardown BEFORE releasing so the close frame our own release emits
      // (e.g. when the mic fails after the WebSocket is already connected) is
      // treated as an echo and ignored by handleProxiedClose.
      claimProxiedTeardown();
      releaseProxiedResources();
      setTalkState('idle');
      setShowRequestTalkConfirm(false);
      setIsCallInProgress(false);
      setCallStatus(TwilioCallStatus.IDLE);
      setCurrentCallId(null);
      resetDialpadState();
    } finally {
      setIsStarting(false);
    }
  }, [
    user?.email,
    user?.id,
    phoneNumber,
    caseRecordId,
    workflowName,
    selectedOrganizationId,
    selectedProgramId,
    activeProvider,
    programNumbers.byProvider,
    createCallMutation,
    resetDialpadState,
    claimProxiedTeardown,
    releaseProxiedResources,
    handleProxiedClose,
    setDialpadMuted,
  ]);

  // End an in-progress proxied call: tear down the mic/WS/player and move to
  // the post-call disposition flow (mirrors the legacy `disconnect` event).
  const endProxiedCall = useCallback(async () => {
    if (!claimProxiedTeardown()) return; // idempotent
    // Winning the teardown claim IS the attribution on this path (BV-2130):
    // whoever wins is whoever ended the call, and here that is the agent.
    reportTerminatedBy(currentCallIdRef.current, 'rep');
    releaseProxiedResources();
    setCallStatus(TwilioCallStatus.DISCONNECTED);
    setIsCallInProgress(false);
    setCallMuted(false);
    setTalkState('idle');
    setShowRequestTalkConfirm(false);
    setDtmfInput('');
    setCallEndTime(new Date());
    setShowEndCallConfirm(false);
    setShowDisposition(true);
  }, [claimProxiedTeardown, releaseProxiedResources, reportTerminatedBy]);

  // Handle showing end call confirmation
  const handleEndCallClick = useCallback(() => {
    setShowEndCallConfirm(true);
  }, []);

  // Handle actual call ending after confirmation
  const handleEndCall = useCallback(() => {
    if (proxiedActiveRef.current) {
      void endProxiedCall();
      return;
    }
    // Record the intent before hanging up (BV-2130). hangUp() makes the SDK emit
    // the same payload-less `disconnect` a remote hangup emits, so this flag is
    // the only thing that distinguishes them; the disconnect handler consumes it.
    legacyLocalHangupRef.current = true;
    voiceService().hangUp();
    setIsCallInProgress(false);
    setCallMuted(false);
    setDtmfInput(''); // Clear DTMF input
    setShowEndCallConfirm(false);
  }, [endProxiedCall]);

  // Handle mute toggle
  const handleMuteToggle = useCallback(() => {
    if (proxiedActiveRef.current) {
      // While the AI agent holds the mic, unmuting means "let me take over" —
      // confirm first, then requestTalk() (the mic stays muted until granted).
      if (talkState === 'revoked') {
        setShowRequestTalkConfirm(true);
        return;
      }
      const next = !callMuted;
      proxiedMutedRef.current = next; // outbound frames are dropped while muted
      setCallMuted(next);
      setDialpadMuted(next);
      return;
    }
    const newMuteState = voiceService().toggleMute();
    setCallMuted(newMuteState);
    setDialpadMuted(newMuteState);
  }, [setDialpadMuted, callMuted, talkState]);

  // Confirmed request to take the mic back while the AI agent is speaking.
  // requestTalk() sends a REQUEST_TALK control frame; the grant arrives
  // asynchronously via onGrant, which flips talkState to 'granted' and
  // auto-unmutes. No-op if the socket isn't open.
  const handleRequestTalkConfirm = useCallback(() => {
    setShowRequestTalkConfirm(false);
    proxiedConnRef.current?.requestTalk();
  }, []);

  // Kill switch for noise cancellation. Routes to the active call's Krisp
  // instance: the call-scheduler MicCapture graph on the proxied path, or the
  // Twilio Device on the legacy path.
  const handleNoiseCancellationToggle = useCallback(async () => {
    const next = !ncEnabled;
    setNcBusy(true);
    try {
      if (proxiedActiveRef.current) {
        // Krisp may be absent if the browser couldn't load it at call start.
        proxiedKrispRef.current?.toggle();
        setNcEnabled(proxiedKrispRef.current?.isEnabled() ?? false);
      } else {
        await voiceService().setNoiseCancellationEnabled(next);
        setNcEnabled(voiceService().isNoiseCancellationEnabled());
      }
    } catch (err) {
      toast.error('Could not toggle noise cancellation');
      captureSentryException(err instanceof Error ? err : new Error(String(err)), {
        feature: 'noise_cancellation',
        attempted_state: next,
      });
    } finally {
      setNcBusy(false);
    }
  }, [ncEnabled]);

  // Handle DTMF
  const handleSendDTMF = useCallback(
    (digit: string) => {
      // Only send DTMF if call is connected
      if (callStatus !== TwilioCallStatus.CONNECTED) return;
      // Proxied mode: the call-scheduler SDK synthesizes the touch-tone and
      // rides it on the same audio WebSocket as the mic (sendAudio). Bypasses
      // the mute frame-drop on purpose — keypad digits still work while muted.
      if (proxiedActiveRef.current) {
        const conn = proxiedConnRef.current;
        if (!conn) return;
        // Chain so rapid/multi-tone sequences play sequentially, not interleaved.
        proxiedDtmfChainRef.current = proxiedDtmfChainRef.current
          .catch(() => {})
          .then(() => conn.sendDtmf(digit))
          .catch(err =>
            // HIPAA: never log the digit value — only that a DTMF send failed.
            captureSentryException(err instanceof Error ? err : new Error(String(err)), {
              feature: 'proxied_dtmf',
            }),
          );
        return;
      }
      voiceService().sendDigits(digit);
    },
    [callStatus],
  );

  // The post-call panel no longer writes anything: the disposition and notes
  // live in the Output section, and the stack was written as it was made.
  // Still tears down the same local per-call state the old Save's tail did
  // (minus the disposition/notes/reachout state that no longer exists) —
  // GlobalDialpad is mounted once at the app root and never unmounts, so
  // without this a reopened dialpad would show the previous call's stale
  // times and status color until the next call's start handlers overwrite them.
  const handleProceed = useCallback(() => {
    setShowDisposition(false);
    setCurrentCallId(null);
    setCallStartTime(null);
    setCallEndTime(null);
    setCallStatus(TwilioCallStatus.IDLE);
    // Which panel is open is per-call state like the rest. Left set, the next dial opens on "Stack call" with the keypad hidden.
    setCallPanelTab('dtmf');
    resetDialpadState();
    closeDialpad();
  }, [resetDialpadState, closeDialpad]);

  // Get status styles
  const statusStyles = getTwilioCallStatusStyle(callStatus);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      ref={dragRef}
      className="fixed z-50 w-[360px] shadow-2xl"
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
      }}
      onMouseDown={handleMouseDown}
    >
      <Card className="overflow-hidden border-border bg-background">
        {/* Header */}
        <div
          className={`dialpad-header flex items-center justify-between border-b border-border p-3 ${statusStyles.headerBg} ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
        >
          <h3 className="text-base font-semibold text-foreground">
            {isCallInProgress || showDisposition ? (
              <div className="flex items-center gap-2">
                <div
                  className={`h-2 w-2 rounded-full ${statusStyles.indicatorColor} ${statusStyles.animation}`}
                />
                <span>{callStatus.charAt(0).toUpperCase() + callStatus.slice(1)}</span>
              </div>
            ) : (
              'Preview'
            )}
          </h3>
          <div className="flex items-center gap-2">
            {/* MOS indicator — visible during active calls */}
            {isCallInProgress && callQuality.current && (
              <MosIndicator
                mos={callQuality.current.mos}
                onClick={() => setShowQualityPanel(prev => !prev)}
              />
            )}
            {/* Show mute/unmute button when call is active and minimized */}
            {isCallInProgress && isMinimized && (
              <Button
                onClick={handleMuteToggle}
                variant={callMuted ? 'destructive' : 'ghost'}
                size="icon"
                className="h-6 w-6"
                title={callMuted ? 'Unmute' : 'Mute'}
              >
                {callMuted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
              </Button>
            )}
            {/* Noise-cancellation kill switch — visible during active calls */}
            {isCallInProgress && isMinimized && showNcToggle && (
              <Button
                onClick={handleNoiseCancellationToggle}
                variant={ncEnabled ? 'ghost' : 'destructive'}
                size="icon"
                className="h-6 w-6"
                disabled={ncBusy}
                title={ncEnabled ? 'Disable noise cancellation' : 'Enable noise cancellation'}
              >
                <AudioLines className="h-4 w-4" />
              </Button>
            )}
            {/* Link icon - navigate back to the original case */}
            {caseRecordId && returnPath && (
              <Button
                onClick={() => navigate(returnPath)}
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                title="Go back to case"
                disabled={location.pathname === returnPath}
              >
                <ExternalLink className="h-4 w-4" />
              </Button>
            )}
            {/* show minimize/maximize*/}
            <Button
              onClick={() => {
                // Use stored dimensions or fallback to defaults
                const width = dialpadDimensions.current?.width || 360;
                const height = dialpadDimensions.current?.height || 620;

                if (!isMinimized) {
                  setPosition({
                    x: window.innerWidth - width - 20,
                    y: window.innerHeight - 80 - 20,
                  });
                } else {
                  // When maximizing, use full dimensions to ensure it fits in viewport
                  setPosition({
                    x: window.innerWidth - width - 20,
                    y: window.innerHeight - height - 20,
                  });
                }
                setIsMinimized(!isMinimized);
              }}
              variant="ghost"
              size="icon"
              className="h-6 w-6"
            >
              {isMinimized ? <Maximize2 className="h-4 w-4" /> : <Minus className="h-4 w-4" />}
            </Button>
            {/* Only show close when NOT in call */}
            {!isCallInProgress && !showDisposition && (
              <Button onClick={closeDialpad} variant="ghost" size="icon" className="h-6 w-6">
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Talk-handoff banner: a full-width strip flush under the header border.
            'revoked' = the AI agent holds the mic; 'granted' = the human has just
            taken the mic and is live. Replaces the old popover — present but
            subtle. 'idle' (no handoff yet) shows nothing. */}
        {talkState === 'revoked' && (
          <div className="flex items-center gap-1.5 border-b border-border bg-success/10 px-5 py-2 text-xs font-medium text-success">
            <span className="h-1.5 w-1.5 rounded-full bg-success" />
            AI Agent is speaking
          </div>
        )}
        {talkState === 'granted' && (
          <div className="flex items-center gap-1.5 border-b border-border bg-primary/10 px-5 py-2 text-xs font-medium text-primary">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary" />
            You&apos;re live — your mic is on
          </div>
        )}

        {/* Microphone permission warning — calls need mic access, so we surface a
            full-width strip under the header and disable the Call button until
            it's granted. */}
        {!isCallInProgress && (micPermission === 'denied' || micPermission === 'unsupported') && (
          <div className="flex items-center gap-1.5 border-b border-border bg-destructive/10 px-5 py-2 text-xs font-medium text-destructive">
            <MicOff className="h-3.5 w-3.5 shrink-0" />
            <span>
              {micPermission === 'unsupported'
                ? 'Microphone access is not available in this browser.'
                : 'Microphone permission is required to make a call. Please allow mic access from the browser settings.'}
            </span>
          </div>
        )}

        {/* Body - Hidden when minimized */}
        {!isMinimized && (
          <div className="p-5">
            {showDisposition ? (
              // Show post-call disposition form
              <PostCallPanel
                callStartTime={callStartTime}
                callEndTime={callEndTime}
                userEmail={user?.email}
                caseRecordId={caseRecordId ?? ''}
                callId={currentCallId}
                scope={stackScope}
                onProceed={handleProceed}
              />
            ) : (
              // Show regular dialpad
              <>
                {/*
                  Carrier choice. The groups come from the phone-number registry:
                  each group is a carrier the program can dial on, so a program
                  with numbers for one carrier gets no control, since a
                  single-option selector is noise. Locked once a call is up — the
                  carrier is fixed at dial time.
                */}
                {availableProviders.length > 1 && (
                  <ProviderToggle
                    options={availableProviders.map(option => ({
                      value: option,
                      label: providerLabel(option, programNumbers.order),
                    }))}
                    value={activeProvider}
                    disabled={isCallInProgress}
                    onChange={setDialpadProvider}
                  />
                )}

                <div className="mb-3">
                  <Label htmlFor="phone" className="text-xs font-medium text-muted-foreground">
                    Phone Number
                  </Label>
                  <div className="relative mt-1">
                    <Input
                      id="phone"
                      type="tel"
                      value={inputValue}
                      onChange={handleInputChange}
                      placeholder="+1234567890"
                      disabled={isCallInProgress}
                      className="pr-10 text-center font-mono text-lg"
                      autoFocus={!isCallInProgress}
                    />
                    {!isCallInProgress && inputValue && (
                      <Button
                        onClick={handleBackspace}
                        variant="ghost"
                        size="icon"
                        className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2"
                      >
                        ⌫
                      </Button>
                    )}
                  </div>
                </div>

                {/* Keypad entry and call stacking share this slot — the panel is
                    narrow, and an agent is doing one or the other. */}
                <Tabs
                  value={callPanelTab}
                  onValueChange={v => setCallPanelTab(v as 'dtmf' | 'stack')}
                  className="mb-5"
                >
                  <TabsList className="grid h-7 w-full grid-cols-2">
                    <TabsTrigger value="dtmf" className="h-6 text-xs">
                      DTMF Input
                    </TabsTrigger>
                    <TabsTrigger
                      value="stack"
                      className="h-6 text-xs"
                      aria-label={stackTabLabel(stackLeft)}
                    >
                      Stack call
                      <StackTabCount count={stackLeft} />
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="dtmf" className="mt-2">
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <Label htmlFor="dtmf" className="text-xs font-medium text-muted-foreground">
                          DTMF Input
                        </Label>
                        <Tabs
                          value={dtmfViewMode}
                          onValueChange={v => setDtmfViewMode(v as 'normal' | 'translated')}
                          className="h-auto"
                        >
                          <TabsList className={`h-6 p-0.5 ${!dtmfInput ? 'opacity-50' : ''}`}>
                            <TabsTrigger
                              value="normal"
                              className="text-xs px-2 py-0.5 h-5"
                              disabled={!dtmfInput || !isCallInProgress}
                            >
                              Text
                            </TabsTrigger>
                            <TabsTrigger
                              value="translated"
                              className="text-xs px-2 py-0.5 h-5"
                              disabled={!dtmfInput || !isCallInProgress}
                            >
                              Codes
                            </TabsTrigger>
                          </TabsList>
                        </Tabs>
                      </div>
                      <div className="relative">
                        <Input
                          id="dtmf"
                          type="text"
                          value={dtmfViewMode === 'normal' ? dtmfInput : convertToDTMF(dtmfInput)}
                          onChange={dtmfViewMode === 'normal' ? handleDtmfInputChange : undefined}
                          readOnly={dtmfViewMode === 'translated'}
                          placeholder={
                            isCallInProgress
                              ? dtmfViewMode === 'normal'
                                ? 'Enter text or numbers'
                                : 'DTMF codes'
                              : 'Call must be active'
                          }
                          disabled={!isCallInProgress}
                          className={`pr-10 text-center font-mono text-lg ${
                            dtmfViewMode === 'translated' ? 'bg-muted' : ''
                          }`}
                          autoFocus={isCallInProgress && dtmfViewMode === 'normal'}
                        />
                        {dtmfInput && isCallInProgress && (
                          <Button
                            onClick={() => setDtmfInput('')}
                            variant="ghost"
                            size="icon"
                            className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2"
                          >
                            ⌫
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Dialpad Grid */}
                    <div className="mb-5 grid grid-cols-3 gap-2">
                      {DIALPAD_BUTTONS.map(({ digit, label }) => (
                        <Button
                          key={digit}
                          onClick={() => handleDigitPress(digit)}
                          variant="secondary"
                          className="h-12 flex flex-col items-center justify-center p-0"
                        >
                          <span className="text-xl font-semibold leading-none">{digit}</span>
                          {label && (
                            <span className="text-[9px] text-muted-foreground mt-1 leading-none">
                              {label}
                            </span>
                          )}
                        </Button>
                      ))}
                    </div>
                  </TabsContent>

                  <TabsContent value="stack" className="mt-2">
                    <StackCallPanel
                      caseRecordId={caseRecordId ?? ''}
                      callId={currentCallId ?? undefined}
                      scope={stackScope}
                      connected={callConnected}
                    />
                  </TabsContent>
                </Tabs>

                {/* IVR Assist selector (BI-982) — proxied calling. Only offered
                    for workflow types the scheduler supports (see ivrAssist
                    constant); other workflows use the legacy dialer. Hidden during
                    an active call. */}
                {!isCallInProgress && ivrAssistSupported && (
                  <div className="mb-3 flex items-center gap-2">
                    <Checkbox
                      id="use-ivr-assist"
                      checked={useProxiedDialer}
                      onCheckedChange={checked => setUseProxiedDialer(checked === true)}
                      disabled={isStarting}
                    />
                    <Label
                      htmlFor="use-ivr-assist"
                      className="cursor-pointer text-xs font-medium text-muted-foreground"
                    >
                      Use IVR Assist
                    </Label>
                  </div>
                )}

                {/* Controls */}
                <div className="flex gap-2">
                  {!isCallInProgress ? (
                    <Button
                      onClick={
                        ivrAssistSupported && useProxiedDialer ? startProxiedCall : handleMakeCall
                      }
                      disabled={isCallButtonDisabled({
                        phoneNumber,
                        isCreatingCall: createCallMutation.isPending,
                        isStarting,
                        micPermission,
                        numbersLoading: programNumbers.isLoading,
                      })}
                      className="flex-1"
                      variant={phoneNumber ? 'default' : 'secondary'}
                    >
                      <Phone className="mr-2 h-4 w-4" />
                      {createCallMutation.isPending || isStarting ? 'Connecting...' : 'Call'}
                    </Button>
                  ) : (
                    <>
                      <Button onClick={handleEndCallClick} variant="destructive" className="flex-1">
                        <PhoneOff className="mr-2 h-4 w-4" />
                        End
                      </Button>
                      {/* The mic button doubles as the talk-handoff control. While
                          the AI agent holds the mic (talkState === 'revoked') the
                          human is auto-muted and the green banner at the top of the
                          dialpad marks the AI as speaking; clicking the mic requests
                          it back (via a confirm dialog). */}
                      <Button
                        onClick={handleMuteToggle}
                        variant={callMuted ? 'destructive' : 'secondary'}
                        size="icon"
                        title={
                          talkState === 'revoked'
                            ? 'AI agent is speaking — click to take over'
                            : callMuted
                              ? 'Unmute'
                              : 'Mute'
                        }
                      >
                        {callMuted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                      </Button>
                      {showNcToggle && (
                        <Button
                          onClick={handleNoiseCancellationToggle}
                          variant={ncEnabled ? 'secondary' : 'destructive'}
                          size="icon"
                          disabled={ncBusy}
                          title={
                            ncEnabled ? 'Disable noise cancellation' : 'Enable noise cancellation'
                          }
                        >
                          <AudioLines className="h-4 w-4" />
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* Call quality debugger panel — toggled by MOS indicator */}
        {showQualityPanel && isCallInProgress && !isMinimized && (
          <CallQualityPanel
            callQuality={callQuality}
            edge={voiceService().networkRegion()}
            onClose={() => setShowQualityPanel(false)}
          />
        )}
      </Card>

      {/* End Call Confirmation Dialog */}
      <EndCallConfirmDialog
        open={showEndCallConfirm}
        onOpenChange={setShowEndCallConfirm}
        onConfirm={handleEndCall}
      />
      <RequestTalkConfirmDialog
        open={showRequestTalkConfirm}
        onOpenChange={setShowRequestTalkConfirm}
        onConfirm={handleRequestTalkConfirm}
      />
    </div>
  );
}
